import { useReducer } from "react";
import { TranslationKeys } from "src/@types/i18next";
import { VinDecodeResponse } from "@modules/vehicle/types/VinDecodeResponse";
import {
  VehicleInformationFieldsInput,
  BuyerSellerFieldsInput,
  FindMyVehicleInformationFormInput,
  SourceInformationFieldsInput,
} from "../types/BillOfSaleFormFields";

export type BillOfSaleSteps =
  | "user-information"
  | "vehicle-details"
  | "buyer-seller-information"
  | "download-section";

export enum BillOfSaleStepActions {
  SetActiveStep = "SetActiveStep",
  CompleteUserInformationStep = "CompleteUserInformationStep",
  CompleteVehicleDetailsStep = "CompleteVehicleDetailsStep",
  CompleteSellerInformationStep = "CompleteSellerInformationStep",
  CompleteBuyerInformationStep = "CompleteBuyerInformationStep",
  CompleteBuyerSellerInformationStep = "CompleteBuyerSellerInformationStep",
  BackStep = "BackStep",
  SaveFormInformation = "SaveFormInformation",
  SetPrivateBillOfSaleId = "SetPrivateBillOfSaleId",
  SetUserType = "SetUserType",
  SetVinDecodeStatus = "SetVinDecodeStatus",
  SetVinDecodedFromQueryParams = "SetVinDecodedFromQueryParams",
  SetVinDecodedInfo = "SetVinDecodedInfo",
}

export type StepsIndexType = {
  [S in BillOfSaleSteps]: number;
};

export const stepsIndex: StepsIndexType = {
  "user-information": 1,
  "vehicle-details": 2,
  "buyer-seller-information": 3,
  "download-section": 4,
} as const;

type UserTypeSeller = "seller";
type UserTypeBuyer = "buyer";
export type TUserType = UserTypeSeller | UserTypeBuyer;

export enum UserType {
  Seller = "seller",
  Buyer = "buyer",
}

export interface CombinedFormInformation {
  vehicleDetails: FindMyVehicleInformationFormInput &
    VehicleInformationFieldsInput;
  buyerInformation: BuyerSellerFieldsInput;
  sellerInformation: BuyerSellerFieldsInput;
  sourceInformation: SourceInformationFieldsInput;
}

export type BillOfSaleStepAction =
  | {
      type: BillOfSaleStepActions.SetActiveStep;
      payload: number;
    }
  | {
      type: BillOfSaleStepActions.CompleteUserInformationStep;
      payload: boolean;
    }
  | {
      type: BillOfSaleStepActions.CompleteVehicleDetailsStep;
      payload: boolean;
    }
  | {
      type: BillOfSaleStepActions.CompleteBuyerSellerInformationStep;
      payload: boolean;
    }
  | {
      type: BillOfSaleStepActions.BackStep;
    }
  | {
      type: BillOfSaleStepActions.SaveFormInformation;
      payload: Partial<CombinedFormInformation>;
    }
  | {
      type: BillOfSaleStepActions.SetPrivateBillOfSaleId;
      payload: string | null;
    }
  | {
      type: BillOfSaleStepActions.SetUserType;
      payload: TUserType;
    }
  | {
      type: BillOfSaleStepActions.SetVinDecodeStatus;
      payload: boolean;
    }
  | {
      type: BillOfSaleStepActions.SetVinDecodedFromQueryParams;
      payload: boolean;
    }
  | {
      type: BillOfSaleStepActions.SetVinDecodedInfo;
      payload: VinDecodeResponse | undefined;
    };

export type BillOfSaleFormState = {
  activeStep: number;
  isUserInformationStepComplete: boolean;
  isVehicleDetailsStepComplete: boolean;
  isBuyerSellerInformationStepComplete: boolean;
  isVinDecodeComplete: boolean;
  isVinDecodedFromQueryParams: boolean;
  userType: TUserType | null;
  formInformation: CombinedFormInformation;
  privateBillOfSaleId: string | null;
  vinDecodedInfo?: VinDecodeResponse;
};

const reducer = (
  state: BillOfSaleFormState,
  action: BillOfSaleStepAction
): BillOfSaleFormState => {
  switch (action.type) {
    case BillOfSaleStepActions.SetActiveStep:
      if ("payload" in action) {
        return {
          ...state,
          activeStep: action.payload,
        } as BillOfSaleFormState;
      }
      return state;

    case BillOfSaleStepActions.CompleteUserInformationStep:
      if ("payload" in action) {
        return {
          ...state,
          isUserInformationStepComplete: action.payload,
        } as BillOfSaleFormState;
      }
      return state;

    case BillOfSaleStepActions.CompleteVehicleDetailsStep:
      if ("payload" in action) {
        return {
          ...state,
          isVehicleDetailsStepComplete: action.payload,
        } as BillOfSaleFormState;
      }
      return state;

    case BillOfSaleStepActions.CompleteBuyerSellerInformationStep:
      if ("payload" in action) {
        return {
          ...state,
          isBuyerSellerInformationStepComplete: action.payload,
        } as BillOfSaleFormState;
      }
      return state;

    case BillOfSaleStepActions.BackStep: {
      const newState = {
        ...state,
        activeStep: state.activeStep - 1,
      } as BillOfSaleFormState;
      if (newState.activeStep === stepsIndex["user-information"]) {
        newState.isUserInformationStepComplete = false;
      } else if (newState.activeStep === stepsIndex["vehicle-details"]) {
        newState.isVehicleDetailsStepComplete = false;
      } else if (
        newState.activeStep === stepsIndex["buyer-seller-information"]
      ) {
        newState.isBuyerSellerInformationStepComplete = false;
      }
      return newState;
    }

    case BillOfSaleStepActions.SaveFormInformation: {
      return {
        ...state,
        formInformation: {
          ...state.formInformation,
          ...action.payload,
        },
      };
    }

    case BillOfSaleStepActions.SetPrivateBillOfSaleId: {
      return {
        ...state,
        privateBillOfSaleId: action.payload,
      };
    }

    case BillOfSaleStepActions.SetUserType: {
      return {
        ...state,
        userType: action.payload,
      };
    }

    case BillOfSaleStepActions.SetVinDecodeStatus: {
      return {
        ...state,
        isVinDecodeComplete: action.payload,
      };
    }

    case BillOfSaleStepActions.SetVinDecodedFromQueryParams: {
      return {
        ...state,
        isVinDecodedFromQueryParams: action.payload,
      };
    }

    case BillOfSaleStepActions.SetVinDecodedInfo: {
      return {
        ...state,
        vinDecodedInfo: action.payload,
      };
    }

    default:
      return state;
  }
};

const initialState: BillOfSaleFormState = {
  activeStep: 1,
  isUserInformationStepComplete: false,
  isVehicleDetailsStepComplete: false,
  isBuyerSellerInformationStepComplete: false,
  isVinDecodeComplete: false,
  isVinDecodedFromQueryParams: false,
  userType: null,
  formInformation: {
    vehicleDetails: {
      vin: "",
      year: null,
      make: "",
      model: "",
      trim: "",
      bodyType: "",
      exteriorColour: "",
      fuelType: "",
      odometer: null,
      sellingPrice: null,
      vehicleSoldDate: null,
      listPrice: null,
    },
    sellerInformation: {
      firstName: "",
      lastName: "",
      streetAddress: "",
      city: "",
      postalCode: "",
      province: {
        abbr: "",
        name: "" as TranslationKeys["common"],
        id: 0,
        fsaAreas: [],
      },
      email: "",
    },
    buyerInformation: {
      firstName: "",
      lastName: "",
      streetAddress: "",
      city: "",
      postalCode: "",
      province: null,
      email: "",
    },
    sourceInformation: {
      sourceAdId: null,
      sourceUserId: null,
      sourceSessionId: null,
      sourceAppId: null,
      sourceLocationId: null,
      sourceCtaId: null,
      email: null,
    },
  },
  privateBillOfSaleId: null,
};

export default function useBillOfSaleFormStepsReducer() {
  return useReducer(reducer, initialState);
}
